import {
  Component,
  Input,
  ViewChild,
  Output,
  EventEmitter,
} from "@angular/core";
import { environment } from "../../../environments/environment";
import { Project } from "../../interfaces/project";
import { Utils } from "../../utils/utils";
import { Retrospective } from "../../interfaces/retrospective";
import { RiskIndicator } from "../../interfaces/riskIndicator";
import { RetrospectiveWizardStartComponent } from "../retrospective-wizard-1-start/retrospective-wizard-1-start.component";
import { RetrospectiveService } from "../../services/retrospective/retrospective.service";
import { RetrospectiveWizardInviteComponent } from "../retrospective-wizard-2-invite/retrospective-wizard-2-invite.component";
import { RetrospectiveWizardVoteComponent } from "../retrospective-wizard-3-vote/retrospective-wizard-3-vote.component";
import { RetrospectiveWizardResultsComponent } from "../retrospective-wizard-4-results/retrospective-wizard-4-results.component";
import { PlanningPokerService } from "../../services/planning-poker/planning-poker.service";
import { SessionService } from "../../services/session/session.service";

@Component({
  selector: "app-retrospective-wizard",
  templateUrl: "./retrospective-wizard.component.html",
  styleUrls: ["./retrospective-wizard.component.scss"],
})
export class RetrospectiveWizardComponent {
  readonly env: any = environment;
  readonly utils: any = Utils;

  @Input() project: Project;
  @Input() riskIndicators: RiskIndicator[];
  @Input() retrospectives: Retrospective[];

  @Output() loadingStarted: EventEmitter<any> = new EventEmitter<any>();
  @Output() loadingFinished: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("StartPage") StartPage: RetrospectiveWizardStartComponent;
  @ViewChild("InvitePage") InvitePage: RetrospectiveWizardInviteComponent;
  @ViewChild("VotePage") VotePage: RetrospectiveWizardVoteComponent;
  @ViewChild("ResultsPage") ResultsPage: RetrospectiveWizardResultsComponent;

  retrospective: Retrospective = {} as Retrospective;
  riskIndicatorSelection = {};

  constructor(
    private sessionService: SessionService,
    private retrospectiveService: RetrospectiveService,
    private planningPokerService: PlanningPokerService
  ) {}

  async continue() {
    let savedStatus = this.sessionService.getRetrospectiveStatus();
    if (savedStatus) {
      this.retrospective = savedStatus.retrospective;
      return await this.start(savedStatus);
    }
  }

  async start(status: {
    currentIndex: number;
    retrospective: Retrospective;
    indicators: RiskIndicator[];
  }) {
    let resume = status !== null;

    if (!status) {
      // Init
      this.retrospective = { projectId: this.project._id } as Retrospective;
      this.riskIndicatorSelection = {};
      this.riskIndicators.forEach(
        (x) => (this.riskIndicatorSelection[x.shortId] = true)
      );

      // Show start page
      var startPageResult = await this.StartPage.show();

      if (!startPageResult) {
        // Retrospective cancelled
        return false;
      }

      // Create retrospective in backend
      this.retrospective = await this.retrospectiveService.create(
        this.retrospective
      );
      this.retrospective.votings = [];

      let selectedRiskIndicators = Object.keys(this.riskIndicatorSelection)
        .filter((shortId) => this.riskIndicatorSelection[shortId] === true)
        .map(
          (shortId) =>
            this.riskIndicators.filter(
              (indicator) => indicator.shortId === shortId
            )[0]
        )
        .sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

      status = {
        currentIndex: -1,
        retrospective: this.retrospective,
        indicators: selectedRiskIndicators,
      };

      this.sessionService.setRetrospectiveStatus(status);
    }

    if (status.currentIndex < 0) {
      // Show invite page
      let inviteResult = await this.InvitePage.show();

      if (!inviteResult) {
        // Retrospective aborted
        this.sessionService.deleteRetrospectiveStatus();
        await this.retrospectiveService.delete(this.retrospective._id);
        return true;
      }
    }

    this.VotePage.loadingFinished.subscribe((_) => this.loadingFinished.emit());

    for (
      let i = Math.max(0, status.currentIndex);
      i < status.indicators.length;
      i++
    ) {
      this.sessionService.setRetrospectiveStatus({
        currentIndex: i,
        retrospective: this.retrospective,
        indicators: status.indicators,
      });
      this.loadingStarted.emit();

      // Show voting page for current risk indicator
      let voteResult = await this.VotePage.show(
        resume,
        status.indicators[i],
        i + 1,
        status.indicators.length
      );

      if (!voteResult) {
        // Retrospective aborted
        this.sessionService.deleteRetrospectiveStatus();
        await this.retrospectiveService.delete(this.retrospective._id);
        return true;
      }

      this.retrospective.votings.push({
        participants: Array.from(
          this.planningPokerService.participants.values()
        ).map((x) => $.extend({}, x)),
        average: this.planningPokerService.average,
        median: this.planningPokerService.median,
        variation: this.planningPokerService.variation,
        agreement: this.planningPokerService.agreement,
        riskIndicatorId: status.indicators[i]._id,
        name: status.indicators[i].name,
      });

      await this.retrospectiveService.save(this.retrospective);
    }

    this.sessionService.deleteRetrospectiveStatus();

    // Show results page
    await this.ResultsPage.show();

    return true;
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/retrospective-wizard/retrospective-wizard.component.ts